/**
 * Forgot Password page — enter email, get a reset link by email.
 * Response is always generic to avoid leaking whether the email is registered.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tv, Mail, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TIDS = {
  email: "forgot-email-input",
  submit: "forgot-submit",
  success: "forgot-success",
  error: "forgot-error",
};

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(`${API}/api/admin/auth/forgot-password`, { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-slate-900 border-slate-800 p-8 shadow-2xl">
        <div className="flex flex-col items-center mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-pink-600 rounded-xl flex items-center justify-center mb-3 shadow-lg shadow-orange-500/20">
            <Tv className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">Reset your password</h1>
          <p className="text-sm text-slate-400 mt-1 text-center">
            Enter the email on your account and we&apos;ll send you a link.
          </p>
        </div>

        {done ? (
          <div
            data-testid={TIDS.success}
            className="flex items-start gap-2 bg-emerald-950/40 border border-emerald-900/50 text-emerald-300 px-4 py-4 rounded-md text-sm mb-5"
          >
            <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold mb-1">Check your inbox</div>
              <div className="text-emerald-400/90">
                If an account exists for <strong>{email}</strong>, a reset link is on its way.
                The link expires in 30 minutes.
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-300 text-sm">
                Email
              </Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="email"
                  data-testid={TIDS.email}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoFocus
                  className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-orange-500"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid={TIDS.error}
                className="flex items-start gap-2 bg-red-950/40 border border-red-900/50 text-red-300 px-3 py-2.5 rounded-md text-sm"
              >
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <Button
              data-testid={TIDS.submit}
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white font-semibold py-2.5 disabled:opacity-50"
            >
              {loading ? "Sending…" : "Send reset link"}
            </Button>
          </form>
        )}

        <div className="mt-5 text-center">
          <Link
            to="/admin/login"
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-orange-400 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
